import { toast } from "react-toastify";

import { isEnabled } from "utils/featureFlags";

import * as loginReducer from "containers/Login/reducer";
import * as setupReducer from "containers/Setup/reducer";
import * as blueprintReducer from "containers/Blueprint/reducer";
import * as projectReducer from "containers/Project/reducer";
import * as deployReducer from "containers/Deployments/reducer";
import * as wizardReducer from "containers/Wizard/reducer";
import * as globalReducer from "containers/App/reducer";
import * as runReducer from "containers/Run/reducer";
import * as homeReducer from "containers/Home/reducer";

import { projectHelpTitle } from "components/UnifiedProject/Help/style.module.css";
export const UPDATE_BASE = "unifiedProjects/UPDATE_BASE";
export const UPDATE_PROJECT = "unifiedProjects/UPDATE_PROJECT";
export const UPDATE_FIELD = "unifiedProjects/UPDATE_FIELD";

export const SELECT_DATA_ADVANCED_OPTIONS = {
  FIELDS: "Fields",
  CALCULATED_FIELDS: "Calculated Fields",
  ADVANCED_MODELS: "Specialized AI Models",
  GALLERY: "View Data"
};

export const getTrainingTitle = (state, id, showModel = false) => {
  const scenarios = projectReducer.getScenarios(state, id);
  const runs = projectReducer.getProjectRuns(state, id);
  const project = projectReducer.getProject(state, id);
  const selectedRunId = id === "new" ? "new" : projectReducer.getRunId(state, id) || (runs && runs[0] && runs[0].id);
  if (runs) {
    if (projectReducer.getIsTraining(state, id)) {
      return "Training...";
    } else {
      if (scenarios && showModel === false) {
        return scenarios.length + (scenarios.length === 1 ? " scenario" : " scenarios");
      } else if (project && project.segments && projectReducer.getIsOverallSegment(state, id, selectedRunId, true)) {
        return project.segments.length - 1 + " subgroups";
      }

      const capModel = projectReducer.getCapModel(state, id, selectedRunId);

      return capModel ? capModel.label : runs[0] && runs[0].models ? runs[0].models.length + " Evaluated" : "";
    }
  } else {
    return "NOT STARTED";
  }
};

export const getOptionalTitle = (state, id) => {
  const selectedRunId = id === "new" ? "new" : projectReducer.getRunId(state, id);
  const setup = setupReducer.getSetup(state, selectedRunId);

  return setup === undefined || Object.keys(setup).length === 0 ? "..." : "OPTIONAL";
};

export const UNIFIED_PROJECT_STEPS = {
  SELECT_DATA: {
    id: "SELECT_DATA",
    label: "Select Data & Blueprint",
    completeLabel: "Review Data & Blueprint",
    showCheck: true,
    title: (state, id) => {
      const selectedRunId = id === "new" ? "new" : projectReducer.getRunId(state, id);
      const setup = setupReducer.getSetup(state, selectedRunId);
      const selectedDataset = setupReducer.getDatasetFromSetup(state, selectedRunId);
      if (setup === undefined || Object.keys(setup).length === 0) {
        return "...";
      }
      if (selectedDataset) {
        return selectedDataset.name;
      } else {
        return "SELECT DATASET";
      }
    }
  },
  REALITY_CHECK: {
    id: "REALITY_CHECK",
    label: "Baseline Behavior",
    completeLabel: "Baseline Behavior",
    showCheck: true,
    disabled: (state, id) => {
      if (getIsEditBlueprint(state, id) === true) {
        return false;
      }

      const maxStep = getMaxStep(state, id);

      if (maxStep >= UNIFIED_PROJECT_STEP_ORDER.indexOf("REALITY_CHECK")) {
        return false;
      }

      return id === "new" && getCurrentStep(state, id) === UNIFIED_PROJECT_STEPS.SELECT_DATA.id;
    },
    showWarn: (state, id) => {
      const selectedRunId = id === "new" ? "new" : projectReducer.getRunId(state, id);

      const setup = setupReducer.getSetup(state, selectedRunId);
      const scenarios = projectReducer.getScenarios(state, id);

      return setup && setup.isFastTrain === true && scenarios && scenarios.length === 0 ? true : false;
    },
    title: (state, id) => {
      const currentStep = getCurrentStep(state, id);

      const selectedRunId = id === "new" ? "new" : projectReducer.getRunId(state, id);

      const setup = setupReducer.getSetup(state, selectedRunId);
      const scenarios = projectReducer.getScenarios(state, id);

      const maxStep = getMaxStep(state, id);

      if (setup === undefined || Object.keys(setup).length === 0) {
        return "...";
      } else if (setup.isFastTrain && scenarios && scenarios.length === 0) {
        return "SKIPPED";
      } else if (
        maxStep >= UNIFIED_PROJECT_STEP_ORDER.indexOf("REALITY_CHECK") ||
        !(id === "new" || projectReducer.getIsLoading(state, id)) ||
        UNIFIED_PROJECT_STEP_ORDER.indexOf("REALITY_CHECK") < UNIFIED_PROJECT_STEP_ORDER.indexOf(currentStep)
      ) {
        return "COMPLETED";
      } else {
        return "NOT STARTED";
      }
    }
  },
  OBJECTIVE: {
    id: "OBJECTIVE",
    label: "Fairness Constraints",
    completeLabel: "Fairness Constraints",
    showCheck: true,
    showWarn: (state, id) => {
      const selectedRunId = id === "new" ? "new" : projectReducer.getRunId(state, id);

      const setup = setupReducer.getSetup(state, selectedRunId);
      const scenarios = projectReducer.getScenarios(state, id);

      return setup && setup.isFastTrain === true && scenarios && scenarios.length === 0 ? true : false;
    },
    disabled: (state, id) => {
      return isNaN(id);
    },
    hidden: (state, id) => {
      return (
        !isEnabled("fairness") ||
        (isNaN(id) && setupReducer.getIsContinuous(state, id)) ||
        projectReducer.getIsContinuous(state, id) ||
        !projectReducer.getEnableFairnessMetrics(state, id)
      );
    },
    title: (state, id) => {
      const selectedRunId = id === "new" ? "new" : projectReducer.getRunId(state, id);

      const setup = setupReducer.getSetup(state, selectedRunId);
      const scenarios = projectReducer.getScenarios(state, id);

      return setup === undefined || Object.keys(setup).length === 0
        ? "..."
        : setup.isFastTrain && scenarios && scenarios.length === 0
          ? "SKIPPED"
          : getTrainingTitle(state, id);
    }
  },
  OPTIMIZE: {
    id: "OPTIMIZE",
    label: "Business Strategy",
    completeLabel: "Business Strategy",
    showCheck: true,
    showWarn: (state, id) => {
      const selectedRunId = id === "new" ? "new" : projectReducer.getRunId(state, id);

      const setup = setupReducer.getSetup(state, selectedRunId);
      const scenarios = projectReducer.getScenarios(state, id);

      return setup && setup.isFastTrain === true && scenarios && scenarios.length === 0 ? true : false;
    },
    disabled: (state, id) => {
      if (getIsEditBlueprint(state, id) === true) {
        return false;
      }

      const maxStep = getMaxStep(state, id);

      if (maxStep >= UNIFIED_PROJECT_STEP_ORDER.indexOf("OPTIMIZE")) {
        return false;
      }

      return (
        id === "new" &&
        (getCurrentStep(state, id) === UNIFIED_PROJECT_STEPS.SELECT_DATA.id ||
          getCurrentStep(state, id) === UNIFIED_PROJECT_STEPS.REALITY_CHECK.id)
      );
    },
    title: (state, id) => {
      const selectedRunId = id === "new" ? "new" : projectReducer.getRunId(state, id);

      const setup = setupReducer.getSetup(state, selectedRunId);
      const scenarios = projectReducer.getScenarios(state, id);

      return setup === undefined || Object.keys(setup).length === 0
        ? "..."
        : setup.isFastTrain && scenarios && scenarios.length === 0
          ? "SKIPPED"
          : getTrainingTitle(state, id);
    }
  },
  BALANCE: {
    id: "BALANCE",
    label: "Enterprise Balancing",
    completeLabel: "Enterprise Balancing",
    showCheck: false,
    disabled: (state, id) => {
      return getIsEditBlueprint(state, id) === true || id === "new" || projectReducer.getIsLoading(state, id);
    },
    hidden: (state, id) => {
      if (!isEnabled("balancer")) {
        return true;
      }
      const linkedProjectId = projectReducer.getLinkedProjectId(state, id);
      return (
        projectReducer.getIsContinuous(state, id) ||
        projectReducer.getIsContinuous(state, linkedProjectId) ||
        !projectReducer.getHasLinkedProject(state, id) ||
        linkedProjectId === undefined
      );
    },
    title: getOptionalTitle
  },
  TRAIN: {
    id: "TRAIN",
    label: "AI Model",
    completeLabel: "AI Model",
    showCheck: true,
    disabled: (state, id) => {
      return getIsEditBlueprint(state, id) === true || id === "new" || projectReducer.getIsLoading(state, id);
    },
    title: (state, id) => {
      const selectedRunId = id === "new" ? "new" : projectReducer.getRunId(state, id);

      const setup = setupReducer.getSetup(state, selectedRunId);

      return setup === undefined || Object.keys(setup).length === 0 ? "..." : getTrainingTitle(state, id, true);
    }
  },
  TEST: {
    id: "TEST",
    label: "Test Predictions",
    disabled: (state, id) => {
      return getIsEditBlueprint(state, id) === true || id === "new" || projectReducer.getIsLoading(state, id);
    },
    title: getOptionalTitle
  },
  FORECAST: {
    id: "FORECAST",
    label: "Forecast",
    disabled: (state, id) => {
      return getIsEditBlueprint(state, id) === true || id === "new" || projectReducer.getIsLoading(state, id);
    },
    title: (state, id) => {
      const selectedRunId = id === "new" ? "new" : projectReducer.getRunId(state, id);

      const setup = setupReducer.getSetup(state, selectedRunId);

      if (projectReducer.getIsTraining(state, id)) {
        return "Training...";
      }

      return setup === undefined || Object.keys(setup).length === 0 ? "..." : " ";
    }
  },
  DEPLOY: {
    id: "DEPLOY",
    label: "Deployment",
    disabled: (state, id) => {
      const scenarios = projectReducer.getScenarios(state, id);

      return (
        getIsEditBlueprint(state, id) === true ||
        id === "new" ||
        projectReducer.getIsLoading(state, id) ||
        !(scenarios && scenarios.length)
      );
    },
    title: getOptionalTitle
  },
  INTEGRATE: {
    id: "INTEGRATE",
    label: "Integration",
    disabled: (state, id) => {
      return (
        getIsEditBlueprint(state, id) === true ||
        id === "new" ||
        projectReducer.getIsLoading(state, id) ||
        !deployReducer.getProjectDeployment(state, id) ||
        !projectReducer.hasDeployedScenario(state, id)
      );
    },
    title: getOptionalTitle
  },
  MONITOR: {
    id: "MONITOR",
    label: "Monitoring & Retraining",
    disabled: (state, id) => {
      const scenarios = projectReducer.getScenarios(state, id);
      return (
        getIsEditBlueprint(state, id) === true ||
        id === "new" ||
        projectReducer.getIsLoading(state, id) ||
        !deployReducer.getProjectDeployment(state, id) ||
        !projectReducer.hasDeployedScenario(state, id)
      );
    },
    title: getOptionalTitle
  }
};

export const UNIFIED_PROJECT_STEP_ORDER = [
  UNIFIED_PROJECT_STEPS.SELECT_DATA.id,
  UNIFIED_PROJECT_STEPS.REALITY_CHECK.id,
  UNIFIED_PROJECT_STEPS.OBJECTIVE.id,
  UNIFIED_PROJECT_STEPS.OPTIMIZE.id,
  UNIFIED_PROJECT_STEPS.TRAIN.id,
  UNIFIED_PROJECT_STEPS.TEST.id,
  UNIFIED_PROJECT_STEPS.BALANCE.id,
  UNIFIED_PROJECT_STEPS.DEPLOY.id,
  UNIFIED_PROJECT_STEPS.INTEGRATE.id,
  UNIFIED_PROJECT_STEPS.MONITOR.id
];

export const UNIFIED_PROJECT_FORECAST_STEP_ORDER = [
  UNIFIED_PROJECT_STEPS.SELECT_DATA.id,
  UNIFIED_PROJECT_STEPS.FORECAST.id
];

export const DEFAULT_UNIFIED_PROJECT = {
  currentStep: UNIFIED_PROJECT_STEPS.SELECT_DATA.id,
  isSavingBlueprint: false,
  isEditBlueprint: false
};

function unifiedProjectReducer(
  state = {
    ["new"]: {
      ...DEFAULT_UNIFIED_PROJECT,
      currentStep: UNIFIED_PROJECT_STEPS.SELECT_DATA.id
    }
  },
  action
) {
  switch (action.type) {
    case UPDATE_BASE:
      return {
        ...state,
        ...action.data
      };
    case UPDATE_PROJECT:
      return {
        ...state,
        [action.key]: action.value
      };
    case UPDATE_FIELD:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          [action.field]: action.value
        }
      };
    default:
      return state;
  }
}

// Selectors
export function isCalculatedFields() {
  return isEnabled("calculated-fields");
}

export function getBase(state) {
  return state.unifiedProject;
}

export function getUnifiedProject(state, id) {
  return getBase(state)[id];
}

export function getCurrentStep(state, id) {
  const project = getUnifiedProject(state, id);
  return project && project.currentStep;
}

export function getMaxStep(state, id) {
  const project = getUnifiedProject(state, id);
  return project && project.maxStep;
}

export function getIsEditBlueprint(state, id) {
  return getUnifiedProject(state, id) ? getUnifiedProject(state, id).isEditBlueprint : undefined;
}

export function getIsAudit(state, id) {
  return getUnifiedProject(state, id) ? getUnifiedProject(state, id).isAudit : undefined;
}

export function getIsSavingBlueprint(state, id) {
  return getUnifiedProject(state, id) ? getUnifiedProject(state, id).isSavingBlueprint : undefined;
}

export function getIsInit(state, id) {
  return getUnifiedProject(state, id) ? getUnifiedProject(state, id).isInit : undefined;
}

export function getBlueprintId(state, id) {
  return projectReducer.getBlueprintId(state, id) !== undefined
    ? projectReducer.getBlueprintId(state, id)
    : setupReducer.getBlueprintId(state, "new") !== undefined
      ? setupReducer.getBlueprintId(state, "new")
      : "newBlueprint";
}

export function getEditBlueprintRunId(state, id) {
  const blueprintId =
    projectReducer.getBlueprintId(state, id) !== undefined ? projectReducer.getBlueprintId(state, id) : "newBlueprint";

  const runId =
    projectReducer.getInnerId(state, id) !== undefined ? projectReducer.getInnerId(state, id) : "editBlueprint";

  return runId;
}

export function getDatasetOptions() {
  return isEnabled("aa-edg")
    ? {
        FIELDS: "Fields",
        CALCULATED_FIELDS: "Calculated Fields",
        ADVANCED_MODELS: "Specialized AI Models",
        GALLERY: "View Data"
      }
    : {
        FIELDS: "Fields",
        CALCULATED_FIELDS: "Calculated Fields",
        ADVANCED_MODELS: "Specialized AI Models"
      };
}

export function getAdvancedDatasetOptions(state, id) {
  const SELECT_DATA_ADVANCED_OPTIONS = getDatasetOptions();

  return Object.values(SELECT_DATA_ADVANCED_OPTIONS).filter((elem) => {
    if (elem === SELECT_DATA_ADVANCED_OPTIONS.CALCULATED_FIELDS) {
      if (id !== undefined && projectReducer.getIsForecastProject(state, id)) {
        return false;
      } else {
        return isCalculatedFields();
      }
    }
    if (elem === SELECT_DATA_ADVANCED_OPTIONS.ADVANCED_MODELS) {
      return false;
    }
    return true;
  });
}

export function getIsHelpOpen(state) {
  return !loginReducer.getItem(state, "unifiedProjectHideHelp");
}

export function getProjectLoadStatus(state, id) {
  const project = getUnifiedProject(state, id);
  return project && project.loadStatus;
}

export function getIsProjectNotFound(state, id) {
  return getProjectLoadStatus(state, id) === "NOT_FOUND";
}

export function getIsSkipToAdvancedOptions(state, id) {
  const project = getUnifiedProject(state, id);
  return project && project.skipToAdvancedOptions;
}

// Actions
export function updateIsHelpOpen() {
  return (dispatch, getState) => {
    if (!getIsHelpOpen(getState())) {
      dispatch(loginReducer.removeItem("unifiedProjectHideHelp"));
    } else {
      dispatch(loginReducer.storeItem("unifiedProjectHideHelp", true));
    }
    return !getIsHelpOpen(getState());
  };
}

export function updateUnifiedProject(id, project) {
  return {
    type: UPDATE_PROJECT,
    key: id,
    value: project
  };
}

export function setProjectIsNotFound(id, isLoadError) {
  return (dispatch, getState) => {
    return dispatch(updateProjectLoadStatus(id, isLoadError ? "NOT_FOUND" : undefined));
  };
}

export function updateProjectLoadStatus(id, status) {
  return {
    type: UPDATE_FIELD,
    key: id,
    field: "loadStatus",
    value: status
  };
}

export function updateMaxStep(id, maxStep) {
  return {
    type: UPDATE_FIELD,
    key: id,
    field: "maxStep",
    value: maxStep
  };
}

export function updateIsSavingBlueprint(id, isSavingBlueprint) {
  return {
    type: UPDATE_FIELD,
    key: id,
    field: "isSavingBlueprint",
    value: isSavingBlueprint
  };
}

export function updateIsAudit(id, isAudit) {
  return {
    type: UPDATE_FIELD,
    key: id,
    field: "isAudit",
    value: isAudit
  };
}

export function updateIsInit(id, isInit) {
  return {
    type: UPDATE_FIELD,
    key: id,
    field: "isInit",
    value: isInit
  };
}

export function updateSkipToAdvancedOptions(id, bool) {
  return {
    type: UPDATE_FIELD,
    key: id,
    field: "skipToAdvancedOptions",
    value: bool
  };
}

export function updateIsEditBlueprint(id, isEditBlueprint, skipReset = false, skipAll = false) {
  return (dispatch, getState) => {
    if (!skipAll) {
      const blueprintId = getBlueprintId(getState(), id);

      const runId = projectReducer.getInnerId(getState(), id);

      const currentSetup = JSON.parse(JSON.stringify(setupReducer.getSetup(getState(), isNaN(runId) ? "new" : runId)));

      if (isEditBlueprint === true) {
        dispatch(blueprintReducer.initSetup(blueprintId, "editBlueprint", currentSetup));
      }

      if (blueprintId === undefined || (blueprintId === "newBlueprint" && isEditBlueprint === true)) {
        const bp = blueprintReducer.getBlueprint(getState(), "newBlueprint");

        if (bp === undefined || Object.keys(bp).length === 1) {
          dispatch(
            blueprintReducer.updateBlueprint("newBlueprint", {
              id: "newBlueprint",
              wizard: {
                customFields: {},
                sensitive: {},
                advancedInputs: {
                  truePositive: undefined,
                  falsePositive: undefined,
                  trueNegative: undefined,
                  falseNegative: undefined
                }
              }
            })
          );
        }
      }

      const blueprint = {
        ...blueprintReducer.getBlueprint(getState(), blueprintId)
      };

      const editBlueprintId = getEditBlueprintRunId(getState(), id);

      const setup = { ...setupReducer.getSetup(getState(), editBlueprintId) };

      if (isEditBlueprint === true) {
        dispatch(setupReducer.updateSetup("tempSetup", setup));
        dispatch(blueprintReducer.updateBlueprint("tempDefault", blueprint));
      } else if (isEditBlueprint === false && skipReset === false) {
        const oldBlueprint = {
          ...blueprintReducer.getBlueprint(getState(), "tempDefault")
        };
        dispatch(blueprintReducer.updateBlueprint(blueprintId, oldBlueprint));

        dispatch(blueprintReducer.checkEvaluator(blueprintId, runId));
      }

      if (isEditBlueprint === false) {
        const oldSetup = {
          ...setupReducer.getSetup(getState(), "tempSetup")
        };
        dispatch(setupReducer.updateSetup(editBlueprintId, oldSetup));

        dispatch(blueprintReducer.checkEvaluator(blueprintId, runId));
      }
    }
    return dispatch({
      type: UPDATE_FIELD,
      key: id,
      field: "isEditBlueprint",
      value: isEditBlueprint
    });
  };
}

export function saveBlueprint(id, isApply) {
  return (dispatch, getState) => {
    const blueprintId = getBlueprintId(getState(), id);

    const runId = getEditBlueprintRunId(getState(), id);

    const setup = {
      ...setupReducer.getSetup(getState(), runId)
    };

    setup.ignored = setupReducer.getIgnored(getState(), runId);
    setup.ignoredValue = setupReducer.getIgnoredValue(getState(), runId);

    dispatch(updateIsSavingBlueprint(id, true));

    const editBlueprintSetup = setupReducer.getSetup(getState(), "editBlueprint");

    const label =
      projectReducer.getInnerId(getState(), id) === undefined
        ? editBlueprintSetup && editBlueprintSetup.label
          ? editBlueprintSetup.label
          : "New Blueprint"
        : setup && setup.label
          ? setup.label
          : "New Blueprint";

    return dispatch(blueprintReducer.saveBlueprint(blueprintId, label, setup, runId, !isApply))
      .then((retBlueprintId) => {
        dispatch(updateIsSavingBlueprint(id, false));
        dispatch(updateIsEditBlueprint(id, false, true));

        const retRunId = runId === "editBlueprint" ? "new" : runId;
        if (isApply) {
          dispatch(blueprintReducer.initSetup(retBlueprintId, retRunId));
          dispatch(setupReducer.updateBlueprintId(retRunId, retBlueprintId));
        }
        dispatch(resetStepProgress(id));
        dispatch(blueprintReducer.deleteBlueprint("newBlueprint"));
      })
      .catch((e) => {
        dispatch(updateIsSavingBlueprint(id, false));
        dispatch(updateIsEditBlueprint(id, false, true));

        if (isApply) {
          dispatch(blueprintReducer.initSetup(blueprintId, "new"));
          dispatch(setupReducer.updateBlueprintId("new", blueprintId));
        }
        dispatch(resetStepProgress(id));
        dispatch(blueprintReducer.deleteBlueprint("newBlueprint"));
      });
  };
}

export function getBlueprint(id) {
  return (dispatch, getState) => {
    return blueprintReducer.getBlueprint(getState(), id);
  };
}

export function updateCurrentStep(id, step) {
  return (dispatch, getState) => {
    const stepIndex = UNIFIED_PROJECT_STEP_ORDER.indexOf(step);

    const maxStep = getMaxStep(getState(), id);

    if (maxStep === undefined || stepIndex > maxStep) {
      dispatch(updateMaxStep(id, stepIndex));
    }

    return dispatch({
      type: UPDATE_FIELD,
      key: id,
      field: "currentStep",
      value: step
    });
  };
}

export function selectStep(id, step) {
  return (dispatch, getState) => {
    dispatch(updateCurrentStep(id, step));
  };
}

export function resetStepProgress(id) {
  return (dispatch, getState) => {
    dispatch(updateCurrentStep(id, UNIFIED_PROJECT_STEP_ORDER[0]));
    dispatch(updateMaxStep(id, 0));
  };
}

export function startModelRun(id, runId, blueprintId, options) {
  return (dispatch, getState) => {
    const args = {
      runId: runId === undefined ? "new" : runId,
      blueprintId: blueprintId,
      isPostRun: id !== "new",
      options: options
    };

    //dispatch(selectStep(id, UNIFIED_PROJECT_STEPS.TRAIN.id));
    return !isNaN(blueprintId)
      ? wizardReducer.STEP_TYPE.SUCCESS.init(dispatch, getState, args)
      : wizardReducer.STEP_TYPE.ACADEMIC_SUCCESS.init(dispatch, getState, args);
  };
}

export function init(id, blueprintId, datasetId) {
  return (dispatch, getState) => {
    // dispatch({
    //   type: UPDATE_FIELD,
    //   key: id,
    //   field: "isEditBlueprint",
    //   value: false,
    // });

    if (id === "new") {
      if (getCurrentStep(getState(), id) !== UNIFIED_PROJECT_STEPS.SELECT_DATA.id) {
        dispatch(updateCurrentStep(id, UNIFIED_PROJECT_STEPS.SELECT_DATA.id));
      }
      const currSetup = setupReducer.getSetup(getState(), id);
      dispatch(blueprintReducer.init("new", blueprintId || currSetup.blueprintId || "academic"))
        .then(() => {
          const selectedDataset = homeReducer.getDataset(getState(), datasetId);
          if (!selectedDataset && isFinite(datasetId)) {
            return dispatch(homeReducer.fetchDataset(datasetId));
          } else {
            return Promise.resolve();
          }
        })
        .then(() => {
          if (isFinite(datasetId)) {
            dispatch(setupReducer.updateDatasetId("new", parseInt(datasetId), true));
          }
          if (getIsEditBlueprint(getState(), id) === true) {
            dispatch(updateIsEditBlueprint(id, true));
          }
        });
    } else {
      //dispatch(updateUnifiedProject(id, DEFAULT_UNIFIED_PROJECT));
      dispatch(updateIsInit(id, false));
      dispatch(globalReducer.updateCurrentProjectId(id));
      return dispatch(projectReducer.init(id)).then(() => {
        const pRunId = projectReducer.getOverallRunId(getState(), id);

        const innerId = projectReducer.getInnerId(getState(), id);
        const runId = innerId !== undefined ? innerId : pRunId;

        if (getCurrentStep(getState(), id) === undefined) {
          if (projectReducer.getIsForecastProject(getState(), id)) {
            dispatch(updateCurrentStep(id, UNIFIED_PROJECT_STEPS.FORECAST.id));
          } else {
            dispatch(updateCurrentStep(id, UNIFIED_PROJECT_STEPS.OPTIMIZE.id));
          }
        }

        if (id) {
          dispatch(projectReducer.reconciliateProjectRejectedModels(id));
        }

        const jobIds = projectReducer.getAllJobIdsFromOptimizer(getState(), id);
        let externalModelErrorWarning = false;
        let externalModelName;

        jobIds.forEach((run) => {
          const expectedExternalModel = runReducer.getExpectedExternalModel(getState(), run);
          if (expectedExternalModel && expectedExternalModel.expected && !expectedExternalModel.actual) {
            if (!externalModelErrorWarning) {
              externalModelErrorWarning = true;
            }
            if (!externalModelName) {
              externalModelName = expectedExternalModel.expected.replace("external-classifier-", "");
            }
          }
        });

        if (externalModelErrorWarning) {
          toast(
            `Something went wrong when evaluating the external model ${externalModelName}. Please check that the url, username and password are correct. Also make sure you have trained the model with the same dataset, target column and success value(s). And look at the notebook for more details.`,
            {
              type: toast.TYPE.ERROR,
              position: toast.POSITION.TOP_CENTER,
              autoClose: false
            }
          );
        }

        if (runId) {
          return dispatch(blueprintReducer.init(pRunId, blueprintId));
        }
        return Promise.resolve();
      });
    }
  };
}

export function reset(id) {
  return (dispatch, getState) => {
    const blueprintIds = ["newBlueprint", "editBlueprint", "tempDefault"];
    const setupIds = ["tempSetup", "editBlueprint"];

    blueprintIds.forEach((bpId) => {
      const currBp = getBlueprintId(getState(), bpId);
      if (currBp) {
        dispatch(blueprintReducer.deleteBlueprint(bpId));
      }
    });

    setupIds.forEach((sId) => {
      const currSetup = setupReducer.getSetup(getState(), sId);
      if (currSetup) {
        dispatch(setupReducer.updateSetup(sId, undefined));
      }
    });

    if (getIsEditBlueprint(getState(), id)) {
      dispatch({
        type: UPDATE_FIELD,
        key: id,
        field: "isEditBlueprint",
        value: false
      });
    }

    if (getIsProjectNotFound(getState(), id)) {
      dispatch(setProjectIsNotFound(id, false));
    }

    dispatch(setupReducer.resetNewSetup());
  };
}

export function reloadProject(projectId) {
  return (dispatch, getState) => {
    dispatch(projectReducer.updateIsLoadingProject(projectId, true));
    return dispatch(projectReducer.fetchProject(projectId)).then(() => {
      return dispatch(projectReducer.init(projectId)).then(() => {
        if (projectReducer.getIsLoadingProject(getState(), projectId)) {
          dispatch(projectReducer.updateIsLoadingProject(projectId, false));
        }
      });
    });
  };
}

export default unifiedProjectReducer;
